import axios from 'axios';
import qs from 'qs';
import { createQuery } from 'react-query-kit';

import { restUrl } from '@/services/utils';

export interface PublicationDestination {
    key: string | number;
    name: string;
    sortKey: string;
}

interface ContactDestination {
    organizationId?: number;
    id?: number;
    name: string;
    state?: string;
    contactRole?: string;
}

interface CommunicationChannelDestination {
    id: number;
    name: string;
    active?: boolean;
    type?: string;
    plan?: string;
    subDestinations?: Record<string, string>;
}

/**
 * Publication destinations for the browse filter dropdown.
 * Mirrors `items-controller.js#definePublishDestinations`: established
 * recipient contacts, verified active channels, and channel sub-destinations.
 */
export const usePublicationDestinations = createQuery<PublicationDestination[]>({
    queryKey: ['browse', 'publicationDestinations'],
    fetcher: async () => {
        const [contactsResponse, channelsResponse, verificationResponse] = await Promise.all([
            axios.get<ContactDestination[]>(restUrl('contacts'), {
                params: {
                    state: 'ESTABLISHED',
                    role: ['DATA_RECIPIENT', 'DATA_SUPPLIER_AND_RECIPIENT'],
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
            }),
            axios.get<CommunicationChannelDestination[]>(restUrl('communicationchannels')),
            axios.get<Record<string, boolean>>(restUrl('communicationchannelTypeVerification')),
        ]);

        const destinations: PublicationDestination[] = [];
        const verification = verificationResponse.data ?? {};

        for (const contact of contactsResponse.data ?? []) {
            if (contact.organizationId == null) {
                continue;
            }
            if (contact.contactRole === 'DATA_SUPPLIER') {
                continue;
            }
            destinations.push({
                key: contact.organizationId,
                name: contact.name,
                sortKey: `1${contact.name}`,
            });
        }

        for (const channel of channelsResponse.data ?? []) {
            if (channel.active === false) {
                continue;
            }

            // Angular: needsVerification = channelTypesVerification && !channelTypesVerification[channel.type]
            if (!verification[channel.type ?? '']) {
                continue;
            }

            destinations.push({
                key: channel.id,
                name: channel.name,
                sortKey: `2${channel.name}`,
            });

            const subDestinations = channel.subDestinations ?? {};
            for (const [subKey, label] of Object.entries(subDestinations)) {
                const display = label && label !== subKey ? `${label} (${subKey})` : subKey;
                destinations.push({
                    key: `${channel.id}:${subKey}`,
                    name: display,
                    sortKey: `2${channel.name}${display}${subKey}`,
                });
            }
        }

        return destinations.sort((a, b) =>
            a.sortKey.toLowerCase().localeCompare(b.sortKey.toLowerCase()),
        );
    },
});
